import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tabs, Tab } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import CloseIcon from '@mui/icons-material/Close';
import { getPendingApprovals, approveSlip, rejectSlip } from '../../api/commissionSlipApi';
import { getPendingSalesEditRequests, approveSalesEditRequest, rejectSalesEditRequest } from '../../api/salesEditApi';
import { getPendingAmendments, approveAmendment, rejectAmendment, getCommissionDetail } from '../../api/commissionAmendmentApi';
import { getPendingPromos, approveAccountant as approvePromoAccountant, approveFinal as approvePromoFinal, rejectPromotion } from '../../api/tierApi';
import { getApplications } from '../../api/invitationApi';
import { getAllExtraSlips, approveExtraSlip, rejectExtraSlip } from '../../api/extraSlipApi';
import AgentApplications from '../accountant/agentApplications';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM } from '../../utils/priceUtils';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';

export default function PendingApprovals() {
  const { user } = useUser();
  const isSuperAdmin = !!user?.roles?.includes('Super Admin');
  const isAccountant = !!user?.roles?.includes('Accountant');
  const [mainTab, setMainTab] = useState('pending');
  const [pendingTab, setPendingTab] = useState('slips');

  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewSlip, setViewSlip] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, slipId: null });
  const [rejectRemarks, setRejectRemarks] = useState('');

  const [editRequests, setEditRequests] = useState([]);
  const [editLoading, setEditLoading] = useState(true);
  const [editRejectDialog, setEditRejectDialog] = useState({ open: false, requestId: null });
  const [editRejectRemarks, setEditRejectRemarks] = useState('');

  const [amendments, setAmendments] = useState([]);
  const [amendLoading, setAmendLoading] = useState(true);
  const [amendRejectDialog, setAmendRejectDialog] = useState({ open: false, amendmentId: null });
  const [amendRejectRemarks, setAmendRejectRemarks] = useState('');
  const [amendDetailDialog, setAmendDetailDialog] = useState({ open: false, amendment: null, currentLines: [], proposedLines: [] });
  const [amendDetailLoading, setAmendDetailLoading] = useState(false);

  // Tier promotions — combined approval queue.
  const [tierPromos, setTierPromos] = useState([]);
  const [tierHistory, setTierHistory] = useState([]);
  const [tierLoading, setTierLoading] = useState(true);
  const [tierRejectDialog, setTierRejectDialog] = useState({ open: false, promotionId: null });
  const [tierRejectRemarks, setTierRejectRemarks] = useState('');

  // Extra slips — pending-approval queue (also approvable from All Slips List).
  const [extraSlips, setExtraSlips] = useState([]);
  const [extraLoading, setExtraLoading] = useState(true);

  // Agent application count for the tab badge — fetched independently so the
  // badge stays accurate even when the user hasn't opened the Agents tab yet.
  const [agentAppsCount, setAgentAppsCount] = useState(0);
  const [agentTabBust, setAgentTabBust] = useState(0); // increments to remount AgentApplications on demand

  const fetchData = useCallback(async () => {
    try { setSlips((await getPendingApprovals()).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchEditRequests = useCallback(async () => {
    try { setEditRequests((await getPendingSalesEditRequests()).data); }
    catch (e) { console.error(e); }
    finally { setEditLoading(false); }
  }, []);

  const fetchAmendments = useCallback(async () => {
    try { setAmendments((await getPendingAmendments()).data); }
    catch (e) { console.error(e); }
    finally { setAmendLoading(false); }
  }, []);

  const fetchTierPromos = useCallback(async () => {
    setTierLoading(true);
    try {
      const [openRes, histRes] = await Promise.all([
        getPendingPromos('All').catch(() => ({ data: [] })),
        getPendingPromos('History').catch(() => ({ data: [] })),
      ]);
      setTierPromos(openRes.data || []);
      setTierHistory(histRes.data || []);
    } catch (e) { console.error(e); }
    finally { setTierLoading(false); }
  }, []);

  const fetchExtraSlips = useCallback(async () => {
    try {
      const data = await getAllExtraSlips();
      setExtraSlips((data || []).filter(s => (s.status || 'Pending') === 'Pending'));
    } catch (e) { console.error(e); }
    finally { setExtraLoading(false); }
  }, []);

  const fetchAgentAppsCount = useCallback(async () => {
    try {
      const res = await getApplications();
      const apps = res.data || [];
      const pending = apps.filter(a => a.status === 'PendingReview' || a.status === 'PendingApproval').length;
      setAgentAppsCount(pending);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); fetchEditRequests(); fetchAmendments(); fetchTierPromos(); fetchExtraSlips(); fetchAgentAppsCount(); }, [fetchData, fetchEditRequests, fetchAmendments, fetchTierPromos, fetchExtraSlips, fetchAgentAppsCount]);

  const handleApprovePromoAccountant = async (id) => {
    if (!window.confirm('Approve this tier promotion (Accountant stage)?')) return;
    try { await approvePromoAccountant(id); fetchTierPromos(); }
    catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const handleApprovePromoFinal = async (id) => {
    if (!window.confirm('Final approval — agent rates and affected sales will be recomputed. Continue?')) return;
    try { await approvePromoFinal(id); fetchTierPromos(); }
    catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const handleRejectPromo = async () => {
    if (!tierRejectRemarks.trim()) { alert('Please enter rejection remarks'); return; }
    try {
      await rejectPromotion(tierRejectDialog.promotionId, tierRejectRemarks);
      setTierRejectDialog({ open: false, promotionId: null });
      setTierRejectRemarks('');
      fetchTierPromos();
    } catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const tierPromoColumns = (forHistory = false) => [
    { id: 'agentName', header: 'Agent', accessorKey: 'userFullName', size: 160 },
    {
      id: 'transition', header: 'Promotion',
      accessorFn: row => `${row.fromTierName ?? '(none)'} → ${row.toTierName} (${row.toTierPscPercent}%)`,
      size: 220,
    },
    {
      id: 'revenue', header: 'Revenue',
      accessorFn: row => row.accumulatedRevenue,
      Cell: ({ row }) => `RM ${Number(row.original.accumulatedRevenue).toLocaleString()}`,
      size: 130,
    },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 170,
      Cell: ({ row }) => {
        const s = row.original.status;
        const map = {
          Pending:            { bg: '#FEF3C7', fg: '#92400E', label: 'Pending Accountant' },
          AccountantApproved: { bg: '#DBEAFE', fg: '#1D4ED8', label: 'Pending Super Admin' },
          Approved:           { bg: '#DCFCE7', fg: '#166534', label: 'Approved' },
          Rejected:           { bg: '#FEE2E2', fg: '#991B1B', label: 'Rejected' },
        };
        const m = map[s] ?? { bg: '#F1F5F9', fg: '#475569', label: s };
        return <Chip label={m.label} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: m.bg, color: m.fg }} />;
      },
    },
    {
      id: 'date', header: forHistory ? 'Resolved' : 'Requested',
      accessorFn: row => forHistory ? (row.finalApprovedOn || row.requestedOn) : row.requestedOn,
      Cell: ({ row }) => convertDateTimeToDateString(forHistory ? (row.original.finalApprovedOn || row.original.requestedOn) : row.original.requestedOn),
      size: 110,
    },
    forHistory
      ? {
          id: 'effective', header: 'Effective From',
          accessorFn: row => row.effectiveFrom,
          Cell: ({ row }) => row.original.effectiveFrom ? convertDateTimeToDateString(row.original.effectiveFrom) : '—',
          size: 120,
        }
      : {
          id: 'actions', header: '', size: 280, enableSorting: false, enableColumnFilter: false,
          Cell: ({ row }) => {
            const p = row.original;
            return (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {p.status === 'Pending' && (isAccountant || isSuperAdmin) && (
                  <Button size="small" variant="contained" color="success" onClick={() => handleApprovePromoAccountant(p.promotionId)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Acct Approve</Button>
                )}
                {p.status === 'AccountantApproved' && isSuperAdmin && (
                  <Button size="small" variant="contained" color="success" onClick={() => handleApprovePromoFinal(p.promotionId)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Final Approve</Button>
                )}
                {(p.status === 'Pending' || p.status === 'AccountantApproved') && (isAccountant || isSuperAdmin) && (
                  <Button size="small" variant="outlined" color="error" onClick={() => setTierRejectDialog({ open: true, promotionId: p.promotionId })} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Reject</Button>
                )}
              </Box>
            );
          },
        },
  ];

  const tierPromoTable = useMaterialReactTable({
    columns: tierPromoColumns(false), data: tierPromos,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  const tierHistoryTable = useMaterialReactTable({
    columns: tierPromoColumns(true), data: tierHistory,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  const handleApproveEditRequest = async (requestId) => {
    if (!window.confirm('Approve this sales edit request?')) return;
    try { await approveSalesEditRequest(requestId); alert('Approved'); fetchEditRequests(); }
    catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const handleRejectEditRequest = async () => {
    if (!editRejectRemarks.trim()) { alert('Please enter rejection remarks'); return; }
    try { await rejectSalesEditRequest(editRejectDialog.requestId, editRejectRemarks); alert('Rejected'); setEditRejectDialog({ open: false, requestId: null }); setEditRejectRemarks(''); fetchEditRequests(); }
    catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const handleViewAmendDetail = async (amendment) => {
    setAmendDetailLoading(true);
    setAmendDetailDialog({ open: true, amendment, currentLines: [], proposedLines: [] });
    try {
      const res = await getCommissionDetail(amendment.salesId);
      const currentLines = res.data.commissionLines || [];
      let proposedLines = [];
      try { proposedLines = JSON.parse(amendment.proposedData); } catch {}
      setAmendDetailDialog(prev => ({ ...prev, currentLines, proposedLines }));
    } catch (e) { console.error(e); }
    finally { setAmendDetailLoading(false); }
  };

  const handleApproveAmendment = async (amendmentId) => {
    if (!window.confirm('Approve this commission amendment?')) return;
    try { await approveAmendment(amendmentId); alert('Approved'); fetchAmendments(); }
    catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const handleRejectAmendment = async () => {
    if (!amendRejectRemarks.trim()) { alert('Please enter rejection remarks'); return; }
    try { await rejectAmendment(amendRejectDialog.amendmentId, amendRejectRemarks); alert('Rejected'); setAmendRejectDialog({ open: false, amendmentId: null }); setAmendRejectRemarks(''); fetchAmendments(); }
    catch (e) { alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message)); }
  };

  const amendmentColumns = [
    { id: 'salesId', header: 'Sale ID', accessorKey: 'salesId', size: 80 },
    { id: 'reason', header: 'Reason', accessorKey: 'reason', size: 250 },
    { id: 'requestedBy', header: 'Requested By', accessorKey: 'requestedByName', size: 140 },
    { id: 'requestDate', header: 'Date', accessorFn: row => row.createdOn, Cell: ({ row }) => convertDateTimeToDateString(row.original.createdOn), size: 110 },
    {
      id: 'actions', header: '', size: 240, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => handleViewAmendDetail(row.original)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>View</Button>
          <Button size="small" variant="contained" color="success" onClick={() => handleApproveAmendment(row.original.amendmentId)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" onClick={() => setAmendRejectDialog({ open: true, amendmentId: row.original.amendmentId })} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Reject</Button>
        </Box>
      ),
    },
  ];

  const amendmentTable = useMaterialReactTable({
    columns: amendmentColumns, data: amendments,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  const editRequestColumns = [
    { id: 'salesId', header: 'Sale ID', accessorKey: 'salesId', size: 80 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 150 },
    {
      id: 'requestType', header: 'Type', accessorKey: 'requestType', size: 100,
      Cell: ({ cell }) => {
        const val = cell.getValue();
        return <Chip label={val} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif",
          backgroundColor: val === 'Archive' ? '#FEE2E2' : '#DBEAFE',
          color: val === 'Archive' ? '#991B1B' : '#1E40AF' }} />;
      },
    },
    { id: 'reason', header: 'Reason', accessorKey: 'reason', size: 200 },
    { id: 'requestedBy', header: 'Requested By', accessorKey: 'requestedByName', size: 140 },
    { id: 'requestDate', header: 'Date', accessorFn: row => row.createdOn, Cell: ({ row }) => convertDateTimeToDateString(row.original.createdOn), size: 110 },
    {
      id: 'actions', header: '', size: 180, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" color="success" onClick={() => handleApproveEditRequest(row.original.requestId)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" onClick={() => setEditRejectDialog({ open: true, requestId: row.original.requestId })} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Reject</Button>
        </Box>
      ),
    },
  ];

  const editRequestTable = useMaterialReactTable({
    columns: editRequestColumns, data: editRequests,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  const handleApprove = async (slipId) => {
    if (!window.confirm('Approve this commission slip?')) return;
    try { await approveSlip(slipId); alert('Approved'); fetchData(); }
    catch (e) { alert('Failed: ' + (e.response?.data || e.message)); }
  };

  const handleReject = async () => {
    if (!rejectRemarks.trim()) { alert('Please enter rejection remarks'); return; }
    try { await rejectSlip(rejectDialog.slipId, rejectRemarks); alert('Rejected'); setRejectDialog({ open: false, slipId: null }); setRejectRemarks(''); fetchData(); }
    catch (e) { alert('Failed: ' + (e.response?.data || e.message)); }
  };

  const getSlipName = (row) => {
    if (!row.period) return '';
    const parts = row.period.split('-');
    const endPart = (parts[1] || parts[0]).trim();
    const parsed = new Date(endPart);
    const monthYear = !isNaN(parsed) ? parsed.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' }) : row.period;
    return row.slipType === 'Astern' ? `Astern Comm Slip (${monthYear})` : `Commission Slip (${monthYear})`;
  };

  const columns = [
    {
      id: 'name', header: 'Name', size: 220,
      accessorFn: row => getSlipName(row),
      Cell: ({ cell, row }) => (
        <span style={{ fontWeight: 600, color: row.original.slipType === 'Astern' ? '#6366F1' : '#0F172A' }}>
          {cell.getValue()}
        </span>
      ),
    },
    { id: 'agentName', header: 'Agent', accessorKey: 'agentName', size: 150 },
    { id: 'period', header: 'Period', accessorKey: 'period', size: 160 },
    { id: 'totalPayout', header: 'Total Payout', accessorFn: row => row.totalPayout, Cell: ({ row }) => handlePriceWithRM(row.original.totalPayout), size: 120 },
    { id: 'dateIssued', header: 'Date Generated', accessorFn: row => row.dateIssued, Cell: ({ row }) => convertDateTimeToDateString(row.original.dateIssued), size: 120 },
    { id: 'generatedBy', header: 'Generated By', accessorKey: 'generatedByName', size: 130 },
    {
      id: 'actions', header: '', size: 200, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setViewSlip(row.original)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>View</Button>
          <Button size="small" variant="contained" color="success" onClick={() => handleApprove(row.original.slipId)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" onClick={() => setRejectDialog({ open: true, slipId: row.original.slipId })} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Reject</Button>
        </Box>
      ),
    },
  ];

  const table = useMaterialReactTable({
    columns, data: slips,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  const handleApproveExtra = async (slip) => {
    if (!window.confirm('Approve this extra slip?')) return;
    try { await approveExtraSlip(slip.slipId ?? slip.extraSlipId, ''); alert('Approved'); fetchExtraSlips(); }
    catch (e) { alert('Failed: ' + (e.response?.data || e.message)); }
  };

  const handleRejectExtra = async (slip) => {
    const r = window.prompt('Reason for rejecting this extra slip?');
    if (r === null) return;
    try { await rejectExtraSlip(slip.slipId ?? slip.extraSlipId, r); alert('Rejected'); fetchExtraSlips(); }
    catch (e) { alert('Failed: ' + (e.response?.data || e.message)); }
  };

  const extraSlipColumns = [
    { id: 'agentName', header: 'Agent', accessorFn: row => row.agentName ?? row.displayName ?? '—', size: 160 },
    { id: 'period', header: 'Period', accessorFn: row => row.period ?? '—', size: 180 },
    { id: 'amount', header: 'Amount', accessorFn: row => row.amount ?? row.totalPayout, Cell: ({ row }) => handlePriceWithRM(row.original.amount ?? row.original.totalPayout), size: 120 },
    { id: 'reason', header: 'Reason', accessorFn: row => row.reason ?? '—', size: 220 },
    {
      id: 'actions', header: '', size: 200, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(isAccountant || isSuperAdmin) && (
            <>
              <Button size="small" variant="contained" color="success" onClick={() => handleApproveExtra(row.original)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Approve</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => handleRejectExtra(row.original)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>Reject</Button>
            </>
          )}
        </Box>
      ),
    },
  ];

  const extraSlipTable = useMaterialReactTable({
    columns: extraSlipColumns, data: extraSlips,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  if (loading && editLoading && amendLoading) return <div>Loading...</div>;

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>Approvals</h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>Slips, sales edits, commission amendments, and tier promotions in one queue.</p>
      </Box>

      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 1.5, borderBottom: '1px solid #E2E8F0' }}>
        <Tab value="pending" label="Pending" sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }} />
        <Tab value="history" label="History" sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }} />
      </Tabs>

      {mainTab === 'history' ? (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {tierLoading ? <div style={{ padding: 16 }}>Loading...</div> : <MaterialReactTable table={tierHistoryTable} />}
        </div>
      ) : (
      <>
      <Tabs value={pendingTab} onChange={(_, v) => { setPendingTab(v); if (v === 'agents') setAgentTabBust(b => b + 1); }} sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }} variant="scrollable" scrollButtons={false}>
        <Tab value="slips"      label={`Commission Slips (${slips.length})`}            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} />
        <Tab value="extra"      label={`Extra Slips (${extraSlips.length})`}            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} />
        <Tab value="edits"      label={`Sales Edit / Archive (${editRequests.length})`} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} />
        <Tab value="amendments" label={`Commission Amendments (${amendments.length})`}  sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} />
        <Tab value="tiers"      label={`Tier Promotions (${tierPromos.length})`}        sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} />
        <Tab value="agents"     label={`Agent Applications (${agentAppsCount})`}        sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} />
      </Tabs>

      {pendingTab === 'slips' && (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <MaterialReactTable table={table} />
        </div>
      )}

      {pendingTab === 'extra' && (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {extraLoading ? <div style={{ padding: 16 }}>Loading...</div> : <MaterialReactTable table={extraSlipTable} />}
        </div>
      )}

      {pendingTab === 'edits' && (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {editLoading ? <div style={{ padding: 16 }}>Loading...</div> : <MaterialReactTable table={editRequestTable} />}
        </div>
      )}

      {/* Reject Sales Edit Request Dialog */}
      <Dialog open={editRejectDialog.open} onClose={() => setEditRejectDialog({ open: false, requestId: null })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>Reject Sales Edit Request</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Rejection Remarks" value={editRejectRemarks} onChange={e => setEditRejectRemarks(e.target.value)} placeholder="Enter reason for rejection..." sx={{ mt: 1 }} InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setEditRejectDialog({ open: false, requestId: null }); setEditRejectRemarks(''); }} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleRejectEditRequest} variant="contained" color="error" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* View Slip Modal */}
      {viewSlip && (
        <Dialog open={!!viewSlip} onClose={() => setViewSlip(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
          <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
            <span>Commission Slip — {viewSlip.agentName}</span>
            <IconButton onClick={() => setViewSlip(null)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Period</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{viewSlip.period}</Typography>
              </Box>
              <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Total Payout</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1A8CFF', fontFamily: "'Poppins', sans-serif" }}>{handlePriceWithRM(viewSlip.totalPayout)}</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontFamily: "'Poppins', sans-serif" }}>Approval Status</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip label={`Approver 1: ${viewSlip.approver1Name || '—'} — ${viewSlip.approver1Status || 'Pending'}`} size="small" color={viewSlip.approver1Status === 'Approved' ? 'success' : viewSlip.approver1Status === 'Rejected' ? 'error' : 'default'} sx={{ fontFamily: "'Poppins', sans-serif" }} />
              <Chip label={`Approver 2: ${viewSlip.approver2Name || '—'} — ${viewSlip.approver2Status || 'Pending'}`} size="small" color={viewSlip.approver2Status === 'Approved' ? 'success' : viewSlip.approver2Status === 'Rejected' ? 'error' : 'default'} sx={{ fontFamily: "'Poppins', sans-serif" }} />
            </Box>
            {(viewSlip.approver1Remarks || viewSlip.approver2Remarks) && (
              <Box sx={{ background: '#FEF2F2', borderRadius: '8px', p: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#991B1B', fontFamily: "'Poppins', sans-serif" }}>
                  Rejection remarks: {viewSlip.approver1Remarks || viewSlip.approver2Remarks}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontFamily: "'Poppins', sans-serif" }}>Commission Items</Typography>
            {(() => { try { const items = JSON.parse(viewSlip.slipData); return items.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #F1F5F9', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}>
                <span style={{ color: '#374151' }}>{item.customerName || item.customer || `Item ${i+1}`} — {item.type || 'PSC'}</span>
                <span style={{ fontWeight: 600, color: '#0F172A' }}>{handlePriceWithRM(item.payout || item.totalComm || 0)}</span>
              </Box>
            )); } catch { return <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8' }}>Unable to parse slip data</Typography>; }})()}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setViewSlip(null)} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {pendingTab === 'amendments' && (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {amendLoading ? <div style={{ padding: 16 }}>Loading...</div> : <MaterialReactTable table={amendmentTable} />}
        </div>
      )}

      {pendingTab === 'tiers' && (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          {tierLoading ? <div style={{ padding: 16 }}>Loading...</div> : <MaterialReactTable table={tierPromoTable} />}
        </div>
      )}

      {pendingTab === 'agents' && (
        // Embedded version of the existing AgentApplications page — drops its own
        // title header so it slots cleanly under the tabs. Bust key remounts the
        // component each time the tab is re-selected so it refetches the latest data.
        <Box sx={{ mt: 1 }}>
          <AgentApplications key={agentTabBust} embedded />
        </Box>
      )}
      </>
      )}

      {/* Reject Tier Promotion Dialog */}
      <Dialog open={tierRejectDialog.open} onClose={() => setTierRejectDialog({ open: false, promotionId: null })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>Reject Tier Promotion</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Rejection Remarks" value={tierRejectRemarks} onChange={e => setTierRejectRemarks(e.target.value)} placeholder="Reason for rejection..." sx={{ mt: 1 }} InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setTierRejectDialog({ open: false, promotionId: null }); setTierRejectRemarks(''); }} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleRejectPromo} variant="contained" color="error" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Commission Amendment Dialog */}
      <Dialog open={amendRejectDialog.open} onClose={() => setAmendRejectDialog({ open: false, amendmentId: null })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>Reject Commission Amendment</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Rejection Remarks" value={amendRejectRemarks} onChange={e => setAmendRejectRemarks(e.target.value)} placeholder="Enter reason for rejection..." sx={{ mt: 1 }} InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setAmendRejectDialog({ open: false, amendmentId: null }); setAmendRejectRemarks(''); }} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleRejectAmendment} variant="contained" color="error" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, slipId: null })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem' }}>Reject Commission Slip</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Rejection Remarks" value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)} placeholder="Enter reason for rejection..." sx={{ mt: 1 }} InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setRejectDialog({ open: false, slipId: null }); setRejectRemarks(''); }} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Amendment Detail Dialog */}
      <Dialog open={amendDetailDialog.open} onClose={() => setAmendDetailDialog({ open: false, amendment: null, currentLines: [], proposedLines: [] })} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          <span>Commission Amendment — Sale #{amendDetailDialog.amendment?.salesId}</span>
          <IconButton onClick={() => setAmendDetailDialog({ open: false, amendment: null, currentLines: [], proposedLines: [] })} size="small"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {amendDetailLoading ? <Typography sx={{ p: 2, fontFamily: "'Poppins', sans-serif" }}>Loading...</Typography> : (
            <>
              <Box sx={{ background: '#FFF7ED', borderRadius: '8px', p: 1.5, mb: 2 }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#92400E', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Reason</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#78350F', fontFamily: "'Poppins', sans-serif" }}>{amendDetailDialog.amendment?.reason}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif", mb: 0.5 }}>Requested by: {amendDetailDialog.amendment?.requestedByName}</Typography>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {/* Current Commission */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', mb: 1, fontFamily: "'Poppins', sans-serif" }}>Current Commission</Typography>
                  {amendDetailDialog.currentLines.filter(l => l.commPayType !== 'Total Commission' && l.commPayType !== 'Webbey Commission').map((line, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, borderBottom: '1px solid #F1F5F9' }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', fontFamily: "'Poppins', sans-serif" }}>{line.paymentToName}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>{line.commPayType} · {line.rate}%</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{handlePriceWithRM(line.commPay)}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 0.5, borderTop: '2px solid #E2E8F0' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', fontFamily: "'Poppins', sans-serif" }}>Total</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                      {handlePriceWithRM(amendDetailDialog.currentLines.filter(l => l.commPayType !== 'Total Commission' && l.commPayType !== 'Webbey Commission').reduce((sum, l) => sum + l.commPay, 0))}
                    </Typography>
                  </Box>
                </Box>

                {/* Arrow */}
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                  <ArrowForwardIcon sx={{ color: '#94A3B8', fontSize: '1.5rem' }} />
                </Box>

                {/* Proposed Commission */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1A8CFF', textTransform: 'uppercase', mb: 1, fontFamily: "'Poppins', sans-serif" }}>Proposed Changes</Typography>
                  {amendDetailDialog.proposedLines.map((line, i) => {
                    const currentLine = amendDetailDialog.currentLines.find(c => c.paymentTo === line.paymentTo && c.commPayType === line.commPayType);
                    const amountChanged = !currentLine || currentLine.commPay !== line.commPay;
                    const rateChanged = currentLine && line.rate !== undefined && currentLine.rate !== line.rate;
                    const changed = amountChanged || rateChanged;
                    return (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, borderBottom: '1px solid #F1F5F9', backgroundColor: changed ? '#EFF6FF' : 'transparent', px: changed ? 0.5 : 0, borderRadius: changed ? '4px' : 0 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', fontFamily: "'Poppins', sans-serif" }}>{line.paymentToName || `User #${line.paymentTo}`}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: rateChanged ? '#1A8CFF' : '#94A3B8', fontWeight: rateChanged ? 600 : 400, fontFamily: "'Poppins', sans-serif" }}>
                            {line.commPayType} · {line.rate != null ? `${line.rate}%` : '—'}
                            {rateChanged && currentLine && (
                              <span style={{ color: '#EF4444', marginLeft: 4 }}>
                                (was {currentLine.rate}%)
                              </span>
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: amountChanged ? '#1A8CFF' : '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{handlePriceWithRM(line.commPay)}</Typography>
                          {amountChanged && currentLine && (
                            <Typography sx={{ fontSize: '0.65rem', color: line.commPay > currentLine.commPay ? '#10B981' : '#EF4444', fontFamily: "'Poppins', sans-serif" }}>
                              {line.commPay > currentLine.commPay ? '+' : ''}{handlePriceWithRM(line.commPay - currentLine.commPay)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 0.5, borderTop: '2px solid #DBEAFE' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1A8CFF', fontFamily: "'Poppins', sans-serif" }}>Total</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1A8CFF', fontFamily: "'Poppins', sans-serif" }}>
                      {handlePriceWithRM(amendDetailDialog.proposedLines.reduce((sum, l) => sum + (l.commPay || 0), 0))}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAmendDetailDialog({ open: false, amendment: null, currentLines: [], proposedLines: [] })} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Close</Button>
          <Button variant="contained" color="success" onClick={() => { setAmendDetailDialog({ open: false, amendment: null, currentLines: [], proposedLines: [] }); handleApproveAmendment(amendDetailDialog.amendment?.amendmentId); }} sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>Approve</Button>
          <Button variant="outlined" color="error" onClick={() => { setAmendDetailDialog({ open: false, amendment: null, currentLines: [], proposedLines: [] }); setAmendRejectDialog({ open: true, amendmentId: amendDetailDialog.amendment?.amendmentId }); }} sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>Reject</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
